import { Elements } from '@stripe/react-stripe-js'
import { loadStripe, Stripe, StripeCardElement } from '@stripe/stripe-js'
import React, { PropsWithChildren, useCallback, useState } from 'react'
import { createStyles, makeStyles, Box, Modal, ButtonBase, Slide } from '@material-ui/core'
import { Button, DynamicMuiIcon, Toaster, Typography, Colors, Pallete } from 'src/components/atoms'
import { SupportedCreditCardBrand } from 'src/components/molecules/SupportedCreditCardBrand'
import { CardForm } from './CardForm'
import {
  useCreateOrganizationPaymentBillingInfoSetupIntent,
  useGetCardInfo
} from 'src/fixtures/modules/organizationPayment/hooks'

const stripePromise = loadStripe(process.env.STRIPE_PUBLIC_KEY || '')

export interface SimplifiedPaymentMethod {
  id: string
  card: {
    brand?: string
    last4?: string
    expMonth?: number
    expYear?: number
  }
}

export interface ModalCreditCardFormProps {
  open: boolean
  width?: string
  onClose: () => void
  onSubmit?: (paymentMethod: SimplifiedPaymentMethod) => void
}

const useStyles = makeStyles(() =>
  createStyles({
    modal: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: Colors.base.black
    }
  })
)

export const ModalCreditCardForm = ({
  open,
  width = '580px',
  onClose,
  onSubmit
}: PropsWithChildren<ModalCreditCardFormProps>) => {
  const [stripe, setStripe] = useState<Stripe | null>()
  const [cardElement, setCardElement] = useState<StripeCardElement | null>()
  const [isRegisteringCard, setIsRegisteringCard] = useState(false)
  const classes = useStyles()
  const { handleCreateSetupIntent } = useCreateOrganizationPaymentBillingInfoSetupIntent()
  const { handleGetCardInfo } = useGetCardInfo()

  const onReady = useCallback((stripe, cardElement) => {
    setStripe(stripe)
    setCardElement(cardElement)
  }, [])

  const onClick = useCallback(async () => {
    if (isRegisteringCard) return
    setIsRegisteringCard(true)

    try {
      if (!stripe || !cardElement) return

      const response = await handleCreateSetupIntent()
      const clientSecret = response?.clientSecret

      if (!clientSecret) return

      const { error, setupIntent } = await stripe.confirmCardSetup(clientSecret, {
        payment_method: {
          card: cardElement
        }
      })

      if (error || !setupIntent || typeof setupIntent.payment_method !== 'string') {
        Toaster.error(error?.message || 'カード情報の登録に失敗しました')
        return
      }

      const paymentMethodId = setupIntent.payment_method
      const paymentMethod = await handleGetCardInfo(paymentMethodId)

      if (!paymentMethod) {
        Toaster.error('カード情報の登録に失敗しました')
        return
      }

      onSubmit?.(paymentMethod)
    } finally {
      setIsRegisteringCard(false)
    }
  }, [stripe, cardElement, isRegisteringCard, handleGetCardInfo, handleCreateSetupIntent, onSubmit])

  return (
    <Modal open={open} onClose={onClose} className={classes.modal}>
      <Slide in={open} direction="up">
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          width={width}
          bgcolor={Pallete.functional.background.default}
          borderRadius="10px"
          border={`1px solid ${Colors.background.silver}`}
          overflow="hidden"
        >
          <Box
            px="24px"
            py="16px"
            borderBottom={`1px solid ${Colors.background.silver}`}
            display="flex"
            justifyContent="space-between"
          >
            <Box display="flex" alignItems="center">
              <Typography variant="h3">クレジットカードのご登録</Typography>
            </Box>
            <ButtonBase onClick={onClose}>
              <DynamicMuiIcon variant="close" color="action" size="28px" />
            </ButtonBase>
          </Box>

          <Box p="24px">
            <Box mb="16px">
              <SupportedCreditCardBrand />
            </Box>
            <Box>
              <Box mb="8px">
                <Typography fontSize="s" fontWeight="bold">
                  カード情報
                </Typography>
              </Box>
              <Elements stripe={stripePromise}>
                <CardForm onReady={onReady} />
              </Elements>
            </Box>
          </Box>

          <Box
            display="flex"
            alignItems="center"
            justifyContent="end"
            py="12px"
            bgcolor={Colors.background.lightGray}
            boxShadow={`inset 0px 1px 0px ${Colors.background.silver}`}
          >
            <Box display="flex" mr="24px">
              <Button kind="neutral" variant="outlined" title="キャンセル" onClick={onClose} />
              <Box ml="12px" />
              <Button title="登録" onClick={onClick} disabled={isRegisteringCard} />
            </Box>
          </Box>
        </Box>
      </Slide>
    </Modal>
  )
}
